import React, { useState, useEffect } from "react"

import { Modal } from "frontend/components/layout"
import FilePicker from "./FilePicker"
import FooterCTA from "./FooterCTA"
import UploadCard from "./UploadCard"
import UploadAgain from "./UploadAgain"
import RecaptchaDisplay from "./RecaptchaDisplay"
import IncentivizedUploadBanner from "./IncentivizedUploadBanner"
import IncentivizedUploadHeading from "./IncentivizedUploadHeading"
import Layout from "./Layout"

import { isIncentivizedUpload, logEvent, logUploadFlowEvent, t, Translation } from "frontend/utils"
import { getLayoutCopy } from "./util/copy"

import styles from "./UploadScreen.module.css"
import "frontend/styles/icons.css"

const UploadScreen = () => {
  const [files, setFiles] = useState({})
  const [showRecaptchaModal, setShowRecaptchaModal] = useState(false)
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(window.upload_config.skip_recaptcha)

  useEffect(() => {
    logEvent("upload_page_load", { source: "redesigned_upload" })
  }, [])

  const filesAsArray = Object.values(files).filter((file) => !file.isDeleted)
  const publishedFiles = filesAsArray.filter((file) => file.isPublished)
  const allPublished = publishedFiles.length > 0 && publishedFiles.length === filesAsArray.length
  const isIncentivized = isIncentivizedUpload()

  const showRecaptcha = () => {
    if (!window.upload_config.skip_recaptcha) {
      setShowRecaptchaModal(true)
    }
  }

  const handleFileSelection = (files) => {
    showRecaptcha()

    const filesAsObject = {}
    files.forEach((file) => {
      filesAsObject[file.uploadId] = file

      logUploadFlowEvent("upload_added")
    })

    setFiles(filesAsObject)
  }

  const handleRecaptchaSuccess = () => {
    setShowRecaptchaModal(false)
    setIsRecaptchaVerified(true)
  }

  const updateFile = (file) => {
    setFiles((oldFiles) => {
      return {
        ...oldFiles,
        [file.uploadId]: { ...file },
      }
    })
  }

  const handleUpload = (file) => {
    updateFile({ ...file, isUploaded: true })
  }

  const handlePublish = (file, privacy) => {
    updateFile({ ...file, isPublished: true, privacy })
  }

  const handleDelete = (file) => {
    updateFile({ ...file, isDeleted: true })
  }

  if (filesAsArray.length > 0 && isRecaptchaVerified) {
    const [heading, cta] = getLayoutCopy(filesAsArray, isIncentivized)

    return (
      <Layout
        heading={heading}
        cta={cta}
        renderBanner={() => (isIncentivized && !allPublished ? <IncentivizedUploadBanner showLead /> : null)}
        renderFooter={() => (allPublished ? <UploadAgain onFileSelection={handleFileSelection} /> : null)}
      >
        {filesAsArray.map((file) => {
          return (
            <UploadCard
              key={file.uploadId}
              file={file}
              onDelete={handleDelete}
              onUpload={handleUpload}
              onPublish={handlePublish}
            />
          )
        })}
      </Layout>
    )
  }

  return (
    <Layout
      heading={isIncentivized ? <IncentivizedUploadHeading /> : t("upload.file_selection.heading")}
      cta={t("upload.file_selection.cta")}
      renderBanner={() => (isIncentivized ? <IncentivizedUploadBanner /> : null)}
      renderFooter={() => (
        <div className={styles.footer}>
          <FooterCTA icon="user-group" cta={t("upload.file_selection.value_prop1")} />
          <FooterCTA icon="upload" cta={t("upload.file_selection.value_prop2")} />
          <FooterCTA icon="search" cta={t("upload.file_selection.value_prop3")} />
          <FooterCTA icon="link" cta={t("upload.file_selection.value_prop4")} />
        </div>
      )}
    >
      <Modal isOpen={showRecaptchaModal}>
        <RecaptchaDisplay onSuccess={handleRecaptchaSuccess} />
      </Modal>

      <FilePicker onFileSelection={handleFileSelection} exposeOriginalFile />
      <p className={styles.descriptionText}>
        {t("upload.file_selection.supported_file_types")}:{" "}
        {window.upload_config.extensions.presentation_extension_descriptions}
      </p>
      <p className={styles.descriptionText}>
        <Translation string="upload.file_selection.agreement" />
      </p>
    </Layout>
  )
}

export default UploadScreen
